import React, { useState, useEffect, useRef } from 'react';
import Confetti from 'react-confetti';
import { makeStyles } from '@material-ui/core/styles';
import { Paper, Typography, Button, Box, IconButton, Collapse } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import StarIcon from '@material-ui/icons/Star';
import { useNavigate } from 'react-router-dom';
import { getSubscriptionDetails, checkActiveSubscription } from './web3/contractInteractions';
import { isDebugFakePremium, getMockSubscriptionDetails } from './web3/debugContractOverrides';
import { toggleDebugFakePremium } from './web3/debugContractOverrides';

const useStyles = makeStyles((theme) => ({
  bannerRoot: {
    position: 'relative',
    width: '100%',
    zIndex: 1000,
    minHeight: '35px', // Add minimum height to prevent layout shifts
  },
  banner: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    backgroundColor: '#4d79ff',
    color: '#ffffff',
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 0,
    padding: theme.spacing(2),
    boxShadow: 'none',
  },
  bannerMobile: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    backgroundColor: '#4d79ff',
    color: '#ffffff',
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 0,
    padding: theme.spacing(0.75),
    boxShadow: 'none',
    minHeight: 'auto',
    '@media (orientation: landscape)': {
      padding: theme.spacing(0.25),
      maxHeight: '24px',
    },
  },
  bannerMobileLandscape: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    backgroundColor: '#4d79ff',
    color: '#ffffff',
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 0,
    padding: theme.spacing(0.1),
    boxShadow: 'none',
    minHeight: 'auto',
    maxHeight: '22px',
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '0 24px',
    gap: theme.spacing(1),
    maxWidth: '900px',
    margin: '0 auto',
  },
  contentMobile: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5),
    '@media (orientation: landscape)': {
      flexDirection: 'row',
      justifyContent: 'space-between',
      gap: theme.spacing(0.25),
      padding: 0,
      margin: 0,
    },
  },
  contentMobileLandscape: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    gap: theme.spacing(0.25),
    padding: 0,
    margin: 0,
  },
  premiumAdTextLandscape: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    fontSize: '0.45rem',
    justifyContent: 'center',
    maxWidth: '70%',
  },
  premiumAdText: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0px',
    fontSize: '1rem',
    width: '75%', /* Reduced width to leave space for button */
  },
  premiumAdTextMobile: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '0.85rem',
    justifyContent: 'center',
    '@media (orientation: landscape)': {
      gap: '2px',
      fontSize: '0.45rem',
      maxWidth: '70%',
    },
  },
  wordSpacing: {
    wordSpacing: '0.3em',
  },
  text: {
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    fontSize: '1rem',
  },
  textMobile: {
    fontWeight: 500,
    fontSize: '0.8rem',
    textAlign: 'left',
    lineHeight: '1.2',
    whiteSpace: 'normal',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '@media (orientation: landscape)': {
      fontSize: '0.45rem',
      lineHeight: '1',
      whiteSpace: 'nowrap',
    },
  },
  textLandscape: {
    fontWeight: 500,
    fontSize: '0.45rem',
    textAlign: 'left',
    lineHeight: '1',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  highlight: {
    fontWeight: 700,
    color: '#ffeb3b',
  },
  button: {
    backgroundColor: '#ffffff',
    color: '#4d79ff',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '4px',
    fontWeight: 'bold',
    fontSize: '1rem',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: '#f0f0f0',
    },
  },
  buttonMobile: {
    backgroundColor: '#ffffff',
    color: '#4d79ff',
    border: 'none',
    padding: '6px 12px',
    borderRadius: '4px',
    fontWeight: 'bold',
    fontSize: '0.8rem',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: '#f0f0f0',
    },
    '@media (orientation: landscape)': {
      padding: '2px 6px',
      fontSize: '0.45rem',
      minWidth: '30px',
      height: '16px',
      borderRadius: '2px',
    },
  },
  buttonMobileLandscape: {
    backgroundColor: '#ffffff',
    color: '#4d79ff',
    border: 'none',
    padding: '2px 4px',
    borderRadius: '2px',
    fontWeight: 'bold',
    fontSize: '0.45rem',
    minWidth: '30px',
    height: '16px',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: '#f0f0f0',
    },
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'white',
  },
  starIcon: {
    marginRight: theme.spacing(1),
    color: '#ffeb3b',
    fontSize: '1.6rem',
  },
  starIconMobile: {
    marginRight: theme.spacing(0.5),
    color: '#ffeb3b',
    fontSize: '1rem',
    '@media (orientation: landscape)': {
      marginRight: theme.spacing(0.25),
      fontSize: '0.6rem',
    },
  },
  starIconLandscape: {
    marginRight: theme.spacing(0.25),
    color: '#ffeb3b',
    fontSize: '0.6rem',
  },
  dismissText: {
    fontSize: '0.7rem',
    opacity: 0.7,
    marginTop: theme.spacing(0.5),
    [theme.breakpoints.down('xs')]: {
      textAlign: 'center',
    },
  }
}));

const PremiumBanner = ({ userAddress, isAuthenticated: propsIsAuthenticated, userID }) => {
  const classes = useStyles();
  const [isPremium, setIsPremium] = useState(false);
  const [windowDimension, setWindowDimension] = useState({ width: window.innerWidth, height: 60 });
  
  // Direct check from localStorage for authentication status
  const localStorageAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
  
  // Use the most permissive check - if any source says we're authenticated, hide the banner
  const isAuthenticated = propsIsAuthenticated || localStorageAuthenticated;
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
  const bannerRef = useRef(null);
  const navigate = useNavigate();

  
  // Effect for confetti dimensions and responsive layout
  useEffect(() => {
    // Setup confetti dimensions handler
    const handleResize = () => {
      if (bannerRef.current) {
        const { width, height } = bannerRef.current.getBoundingClientRect();
        setWindowDimension({ width, height });
        setIsMobile(window.innerWidth <= 600);
      }
    };
    
    // Set initial dimensions
    handleResize();
    
    // Add event listener for window resize
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Force immediate check for authentication from all sources
  useEffect(() => {
    // This effect only handles authentication status changes
    // Check all possible authentication sources
    const authFromContext = propsIsAuthenticated;
    const authFromLocalStorage = localStorage.getItem('isAuthenticated') === 'true';
    const authFromUsername = !!localStorage.getItem('username');
    const authFromUserID = !!localStorage.getItem('userID');
    
    console.log('PremiumBanner - Auth Sources:', { 
      context: authFromContext, 
      localStorage: authFromLocalStorage,
      username: authFromUsername,
      userID: authFromUserID
    });
    
    // If ANY source indicates the user is authenticated, hide the banner
    const userIsAuthenticated = authFromContext || authFromLocalStorage || 
                              (authFromUsername && authFromUserID);
    
    if (userIsAuthenticated) {
      console.log('🔑 PremiumBanner: User is authenticated - forcing HIDE');
      setIsPremium(true);
    }
  }, [propsIsAuthenticated]);
  
  // Effect for premium status check
  useEffect(() => {
    const checkPremiumStatus = async () => {
      console.log('PremiumBanner: Checking premium status...');
      console.log('PremiumBanner: isAuthenticated =', isAuthenticated);
      console.log('PremiumBanner: userAddress =', userAddress);
      console.log('PremiumBanner: userID =', userID);
      
      // Check if debug fake premium mode is active
      if (isDebugFakePremium()) {
        console.log('PremiumBanner: Debug mode active - Using mock premium subscription');
        const mockDetails = getMockSubscriptionDetails(userAddress);
        console.log('PremiumBanner: Mock details =', mockDetails);
        setIsPremium(true);
        return;
      }
      
      // TEMPORARY FIX: Assume premium status for authenticated users
      if (isAuthenticated) {
        console.log('PremiumBanner: User is authenticated - setting premium to TRUE by default');
        setIsPremium(true);
        return;
      }

      // Normal flow - only run this if we're not taking the shortcut above
      // Keeping this code for when we want to properly check subscriptions again
      if (false && isAuthenticated && userAddress) { // Disabled with false &&
        console.log('PremiumBanner: User is authenticated with address, checking subscription...');
        try {
          // First check with simplified method
          const hasActive = await checkActiveSubscription(userAddress);
          console.log('PremiumBanner: hasActiveSubscription returned:', hasActive);
          
          // Then get detailed information
          const subscriptionDetails = await getSubscriptionDetails(userAddress);
          console.log('PremiumBanner: Subscription details:', subscriptionDetails);
          
          const isPremiumActive = subscriptionDetails && subscriptionDetails.isActive;
          console.log('PremiumBanner: Setting isPremium to:', isPremiumActive);
          
          setIsPremium(isPremiumActive);
        } catch (error) {
          console.error('PremiumBanner: Error checking premium status:', error);
          setIsPremium(false);
        }
      } else if (!isAuthenticated) {
        console.log('PremiumBanner: User not authenticated');
        setIsPremium(false);
      }
    };
    
    checkPremiumStatus();
  }, [isAuthenticated, userAddress, userID]);

  const handleSubscribe = () => {
    console.log('PremiumBanner: LEARN MORE button clicked');
    
    if (isAuthenticated) {
      console.log('PremiumBanner: Using global handler for authenticated user');
      // Use our global handler that ensures auth state is preserved
      if (window.navigateToSignup) {
        window.navigateToSignup();
      } else {
        // Fallback if global handler isn't available
        console.log('PremiumBanner: Global handler not found, using context-aware approach');
        // Make sure we preserve the existing username (email) from localStorage
        const currentUsername = localStorage.getItem('username');
        
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('userID', userID || '');
        localStorage.setItem('username', currentUsername || userID || ''); // Preserve email as username
        window.location.href = '/signup';
      }
    } else {
      // For non-authenticated users, regular navigation is fine
      console.log('PremiumBanner: Non-authenticated user, using React Router');
      navigate('/signup');
    }
  };

  const isLandscapeOrientation = window.innerHeight < window.innerWidth;

  const landscapeStyles = isLandscapeOrientation ? {
    banner: {
      padding: '2px',
      maxHeight: '22px',
    },
    content: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: 0,
      margin: 0,
      gap: '2px',
    },
    textContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '2px',
      maxWidth: '60%',
    },
    starIcon: {
      fontSize: '0.5rem',
      marginRight: '2px',
    },
    text: {
      fontSize: '0.35rem',
      lineHeight: '0.8',
      whiteSpace: 'nowrap',
    },
    button: {
      padding: '1px 4px',
      fontSize: '0.42rem',
      minWidth: '26px',
      height: '14px',
      borderRadius: '2px',
    },
  } : {};

  const renderDesktopContent = () => (
    <div className={classes.content}>
      <div className={classes.premiumAdText}>
        <div style={{display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', width: '100%'}}>
          <StarIcon className={classes.starIcon} />
          <Typography variant="body1" className={classes.text}>
            Unlock&nbsp;<span className={classes.highlight}>PREMIUM FEATURES</span>
          </Typography>
          <StarIcon className={classes.starIcon} />
        </div>
        <Typography variant="body1" className={classes.text}>
          Free NFT minting, advanced analytics & zero ads!
        </Typography>
      </div>
      <Button 
        variant="contained" 
        className={classes.button} 
        onClick={handleSubscribe}
        style={{minWidth: '120px', marginRight: '10px'}}
      >
        {isAuthenticated ? 'UPGRADE NOW' : 'LEARN MORE'}
      </Button>
    </div>
  );

  // Render mobile content with proper landscape detection
  const renderMobileContent = () => {
    const isLandscapeOrientation = window.innerHeight < window.innerWidth;
    
    return (
      <div 
        className={classes.contentMobile}
        style={isLandscapeOrientation ? landscapeStyles.content : {}}
      >
        <div 
          className={classes.premiumAdTextMobile}
          style={isLandscapeOrientation ? landscapeStyles.textContainer : {}}
        >
          <StarIcon 
            className={classes.starIconMobile} 
            style={isLandscapeOrientation ? landscapeStyles.starIcon : {}}
          />
          <Typography 
            variant="body2" 
            className={classes.textMobile}
            style={isLandscapeOrientation ? landscapeStyles.text : {}}
          >
            {!isLandscapeOrientation && 'Unlock '}
            <span className={classes.highlight}>premium</span>
            {!isLandscapeOrientation && ': Free minting & zero ads'}
          </Typography>
        </div>
        <Button
          variant="contained"
          color="primary"
          size="small"
          className={classes.subscribeButton}
          onClick={handleSubscribe}
          style={{ fontSize: '0.6rem', padding: '2px 6px', minHeight: '16px', lineHeight: '1', ...landscapeStyles.button }}
        >
          {isAuthenticated ? 'Upgrade' : (isLandscapeOrientation ? 'Subscribe' : 'Subscribe Now')}
        </Button>
      </div>
    );
  };
  
  // TESTING DEBUG FUNCTION: Enable fake premium mode by pressing Alt+P
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.altKey && event.key === 'p') {
        const isActive = toggleDebugFakePremium();
        console.log(`DEBUG MODE: Premium subscription ${isActive ? 'ACTIVATED' : 'DEACTIVATED'}`);
        setIsPremium(isActive);
        
        // Force re-render
        if (isActive) {
          alert('Debug premium mode ACTIVATED - Banner should disappear');
        } else {
          alert('Debug premium mode DEACTIVATED - Banner should appear');
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  
  // Don't render the banner if user has premium status
  if (isPremium) {
    console.log('PremiumBanner: User has premium status, hiding premium banner');
    return null;
  }
  
  return (
    <div id='premiumBannerContainer' className={classes.bannerRoot}>
      <Paper 
        elevation={0} 
        className={isMobile ? classes.bannerMobile : classes.banner} 
        style={isMobile && window.innerHeight < window.innerWidth ? {
          padding: '2px',
          maxHeight: '22px',
        } : {}}
        ref={bannerRef}
      >
        {/* Confetti effect */}
        <Confetti
          width={windowDimension.width}
          height={windowDimension.height}
          numberOfPieces={isMobile ? (isLandscapeOrientation ? 10 : 20) : 60}
          recycle={true}
          colors={['#ffeb3b', '#ffffff', '#e3f2fd', '#4fc3f7', '#b3e5fc']}
          gravity={0.08}
          confettiSource={{ x: 0, y: 0, w: windowDimension.width, h: 0 }}
        />
        {isMobile ? renderMobileContent() : renderDesktopContent()}
      </Paper>
    </div>
  );
};

export default PremiumBanner;
